import {
  Badge,
  Box,
  Checkbox,
  Flex,
  Heading,
  Input,
  Spinner,
  Stack,
  Table,
  Text,
  Textarea,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiArrowLeft,
  FiBookOpen,
  FiEdit2,
  FiPlus,
  FiTrash2,
} from "react-icons/fi"

import { OpenAPI, PassportTemplatesService } from "@/client"
import {
  GeofencePicker,
  type GeofenceValue,
} from "@/components/Common/GeofencePicker"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { useOrganization } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/admin-dpp/templates")({
  component: AdminDppTemplates,
})

// ---------------------------------------------------------------------------
// Local types — generated client doesn't know about rules /
// auto_bind_tag_template_id yet. Backend shape lives in
// backend/app/models/dpp.py::_validate_rules.
// ---------------------------------------------------------------------------
type TimeUnit = "minutes" | "hours" | "days"

interface RuleGeoBlock {
  lat: number
  lon: number
  radius_m: number
  address?: string
}

interface RuleTimeBlock {
  after_amount: number
  after_unit: TimeUnit
}

interface DppRule {
  enabled: boolean
  label: string | null
  redirect_url: string
  // When True the public DPP page also navigates to redirect_url on
  // match. When False (default) only the MQTT TAG_REGISTERED push uses
  // the URL — the rendered passport keeps showing.
  redirect_public_page: boolean
  geo?: RuleGeoBlock
  time?: RuleTimeBlock
}

type TagTemplateRow = {
  id: string
  name: string
  tag_type_match?: string | null
}

// Generated PassportTemplateRead doesn't carry the new fields yet, so we
// type the row that this page actually receives.
type PassportTemplateRow = {
  id: string
  name: string
  enabled: boolean
  is_default: boolean
  auto_bind_chip_manufacturer?: string | null
  auto_bind_cin_brand?: string | null
  auto_bind_nfc_ic_manufacturer?: string | null
  auto_bind_tag_type?: string | null
  auto_bind_tag_template_id?: string | null
  rules?: DppRule[]
  default_dpp_record_id?: string | null
}

function emptyGeoBlock(): RuleGeoBlock {
  return { lat: 45.5017, lon: -73.5673, radius_m: 100 }
}

function emptyTimeBlock(): RuleTimeBlock {
  return { after_amount: 30, after_unit: "days" }
}

function emptyRule(): DppRule {
  return {
    enabled: true,
    label: "",
    redirect_url: "",
    redirect_public_page: false,
    geo: emptyGeoBlock(),
  }
}

function authedFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

function AdminDppTemplates() {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  const { showToast } = useCustomToast()
  const orgId = currentOrganization?.id

  const templatesQuery = useQuery({
    queryKey: ["passport-templates", orgId],
    queryFn: () =>
      PassportTemplatesService.listPassportTemplates({
        organisationId: orgId!,
        limit: 200,
      }),
    enabled: !!orgId,
    staleTime: 30_000,
  })

  const tagTemplatesQuery = useQuery({
    queryKey: ["tag-templates-for-dpp", orgId],
    queryFn: async (): Promise<TagTemplateRow[]> => {
      // Backend returns a raw list[TagTemplateRead] — no {data: [...]}
      // wrapper (backend/app/api/routes/tag.py read_organization_templates).
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/tag-templates/?limit=200`,
      )
      if (!r.ok) return []
      const data = (await r.json()) as TagTemplateRow[]
      return (data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name))
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })

  // QDatDroid identifier catalog — used to populate the auto-bind
  // pulldowns below. Shape is {by_category:
  // {tag_type|chip_manufacturer|cin_brand|nfc_ic_manufacturer: [{id,...}]}}.
  // See backend/app/core/tag_catalog.py::catalog_payload.
  const catalogQuery = useQuery({
    queryKey: ["dpp-tag-catalog", orgId],
    queryFn: async (): Promise<{
      by_category: {
        tag_type: { id: string }[]
        chip_manufacturer: { id: string }[]
        cin_brand: { id: string }[]
        nfc_ic_manufacturer: { id: string }[]
      }
    } | null> => {
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/tag-templates/catalog`,
      )
      if (!r.ok) return null
      return r.json()
    },
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  })

  const catalogOptions = useMemo(() => {
    const c = catalogQuery.data?.by_category
    const ids = (xs?: { id: string }[]) =>
      (xs ?? [])
        .map((x) => x.id)
        .filter((v): v is string => !!v)
        .sort((a, b) => a.localeCompare(b))
    return {
      tag_type: ids(c?.tag_type),
      chip_manufacturer: ids(c?.chip_manufacturer),
      cin_brand: ids(c?.cin_brand),
      nfc_ic_manufacturer: ids(c?.nfc_ic_manufacturer),
    }
  }, [catalogQuery.data])

  const rows = (templatesQuery.data?.data ??
    []) as unknown as PassportTemplateRow[]
  const tagTemplates = tagTemplatesQuery.data ?? []
  const tagTemplateById = useMemo(
    () => new Map(tagTemplates.map((tt) => [tt.id, tt] as const)),
    [tagTemplates],
  )

  // ----- Dialog state — null editingId means create mode -----
  const [isOpen, setIsOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState("")
  const [draftTagTemplateId, setDraftTagTemplateId] = useState<string>("")
  const [draftEnabled, setDraftEnabled] = useState(true)
  const [draftChipManufacturer, setDraftChipManufacturer] = useState("")
  const [draftCinBrand, setDraftCinBrand] = useState("")
  const [draftNfcIcManufacturer, setDraftNfcIcManufacturer] = useState("")
  const [draftTagType, setDraftTagType] = useState("")
  const [draftDefaultDppRecordId, setDraftDefaultDppRecordId] = useState("")
  const [draftRules, setDraftRules] = useState<DppRule[]>([])

  // DppRecord catalogue for the default-record dropdown. Cheap to
  // fetch (handful of rows per org) and refreshed when the records
  // page changes things.
  const recordsQuery = useQuery({
    queryKey: ["dpp-records-for-templates", orgId],
    queryFn: async (): Promise<{ id: string; gs1_sku: string }[]> => {
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/dpp-records/?limit=500`,
      )
      if (!r.ok) return []
      const j = (await r.json()) as {
        data?: { id: string; gs1_sku: string }[]
      }
      return j.data ?? []
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })
  const dppRecords = recordsQuery.data ?? []
  // Per-rule checkbox state lives by rule index. We rebuild the set
  // whenever the draftRules length changes so deletes don't leak
  // stale indices.
  const [selectedRuleIndices, setSelectedRuleIndices] = useState<Set<number>>(
    new Set(),
  )

  // ----- Templates table bulk-select -----
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(
    new Set(),
  )

  const resetDraft = () => {
    setEditingId(null)
    setDraftName("")
    setDraftTagTemplateId("")
    setDraftEnabled(true)
    setDraftChipManufacturer("")
    setDraftCinBrand("")
    setDraftNfcIcManufacturer("")
    setDraftTagType("")
    setDraftDefaultDppRecordId("")
    setDraftRules([])
    setSelectedRuleIndices(new Set())
  }

  const seedDraftFromTemplate = (tpl: PassportTemplateRow) => {
    setEditingId(tpl.id)
    setDraftName(tpl.name)
    setDraftTagTemplateId(tpl.auto_bind_tag_template_id ?? "")
    setDraftEnabled(tpl.enabled)
    setDraftChipManufacturer(tpl.auto_bind_chip_manufacturer ?? "")
    setDraftCinBrand(tpl.auto_bind_cin_brand ?? "")
    setDraftNfcIcManufacturer(tpl.auto_bind_nfc_ic_manufacturer ?? "")
    setDraftTagType(tpl.auto_bind_tag_type ?? "")
    setDraftDefaultDppRecordId(tpl.default_dpp_record_id ?? "")
    setDraftRules(
      (tpl.rules ?? []).map((r) => ({
        enabled: r.enabled ?? true,
        label: r.label ?? "",
        redirect_url: r.redirect_url ?? "",
        redirect_public_page: !!r.redirect_public_page,
        geo: r.geo,
        time: r.time,
      })),
    )
    setSelectedRuleIndices(new Set())
    setIsOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("No organisation selected")
      const rulesPayload = draftRules.map((r) => ({
        enabled: r.enabled,
        label: r.label?.trim() || null,
        redirect_url: r.redirect_url.trim(),
        redirect_public_page: r.redirect_public_page,
        ...(r.geo ? { geo: r.geo } : {}),
        ...(r.time ? { time: r.time } : {}),
      }))
      if (editingId === null) {
        const body = {
          organisation_id: orgId,
          name: draftName.trim(),
          enabled: draftEnabled,
          is_default: false,
          sections: [],
          visible_fields: ["name", "epcid"],
          auto_bind_tag_template_id: draftTagTemplateId || null,
          auto_bind_chip_manufacturer: draftChipManufacturer.trim() || null,
          auto_bind_cin_brand: draftCinBrand.trim() || null,
          auto_bind_nfc_ic_manufacturer: draftNfcIcManufacturer.trim() || null,
          auto_bind_tag_type: draftTagType.trim() || null,
          rules: rulesPayload,
          default_dpp_record_id: draftDefaultDppRecordId || null,
        }
        const res = await authedFetch(
          `/api/v1/organizations/${orgId}/passport-templates/`,
          { method: "POST", body: JSON.stringify(body) },
        )
        if (!res.ok)
          throw new Error((await res.text()) || `Create failed: ${res.status}`)
        return res.json()
      }
      const body = {
        name: draftName.trim(),
        enabled: draftEnabled,
        auto_bind_tag_template_id: draftTagTemplateId || null,
        auto_bind_chip_manufacturer: draftChipManufacturer.trim() || null,
        auto_bind_cin_brand: draftCinBrand.trim() || null,
        auto_bind_nfc_ic_manufacturer: draftNfcIcManufacturer.trim() || null,
        auto_bind_tag_type: draftTagType.trim() || null,
        rules: rulesPayload,
        default_dpp_record_id: draftDefaultDppRecordId || null,
      }
      const res = await authedFetch(
        `/api/v1/organizations/${orgId}/passport-templates/${editingId}`,
        { method: "PUT", body: JSON.stringify(body) },
      )
      if (!res.ok)
        throw new Error((await res.text()) || `Save failed: ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["passport-templates", orgId] })
      showToast(
        editingId === null
          ? t("templates.createdToastTitle", "Template created")
          : t("templates.updatedToastTitle", "Template updated"),
        t("templates.savedToastBody", "Passport template saved."),
        "success",
      )
      setIsOpen(false)
      resetDraft()
    },
    onError: (err: unknown) => {
      showToast(
        t("templates.saveErrorTitle", "Could not save template"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const deleteTemplatesMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!orgId) throw new Error("No organisation selected")
      // No bulk endpoint on the backend yet — serialise one DELETE per id.
      // Small N (operator-driven) so this is fine.
      const results = await Promise.all(
        ids.map((id) =>
          authedFetch(
            `/api/v1/organizations/${orgId}/passport-templates/${id}`,
            { method: "DELETE" },
          ),
        ),
      )
      const failed = results.filter((r) => !r.ok)
      if (failed.length > 0) {
        const detail = await failed[0].text()
        throw new Error(
          detail || `Delete failed (${failed.length}/${ids.length})`,
        )
      }
    },
    onSuccess: (_unused, ids) => {
      queryClient.invalidateQueries({ queryKey: ["passport-templates", orgId] })
      setSelectedTemplateIds(new Set())
      // Close the dialog if the row that was being edited has just been
      // deleted via the dialog footer.
      if (editingId && ids.includes(editingId)) {
        setIsOpen(false)
        resetDraft()
      }
      showToast(
        t("templates.deletedToastTitle", "Template deleted"),
        t("templates.deletedToastBody", "{{count}} template(s) removed.", {
          count: ids.length,
        }),
        "success",
      )
    },
    onError: (err: unknown) => {
      showToast(
        t("templates.deleteErrorTitle", "Could not delete template"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const addRule = () => setDraftRules((rs) => [...rs, emptyRule()])
  const removeRule = (idx: number) => {
    setDraftRules((rs) => rs.filter((_, i) => i !== idx))
    setSelectedRuleIndices(new Set())
  }
  const removeSelectedRules = () => {
    if (selectedRuleIndices.size === 0) return
    setDraftRules((rs) => rs.filter((_, i) => !selectedRuleIndices.has(i)))
    setSelectedRuleIndices(new Set())
  }
  const patchRule = (idx: number, patch: Partial<DppRule>) =>
    setDraftRules((rs) =>
      rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    )
  const toggleRuleSelection = (idx: number, next: boolean) => {
    setSelectedRuleIndices((prev) => {
      const out = new Set(prev)
      if (next) out.add(idx)
      else out.delete(idx)
      return out
    })
  }
  const toggleAllRulesSelected = (next: boolean) => {
    if (!next) {
      setSelectedRuleIndices(new Set())
      return
    }
    setSelectedRuleIndices(new Set(draftRules.map((_, i) => i)))
  }
  const allRulesSelected =
    draftRules.length > 0 && selectedRuleIndices.size === draftRules.length

  // Templates table selection helpers
  const toggleTemplateSelection = (id: string, next: boolean) => {
    setSelectedTemplateIds((prev) => {
      const out = new Set(prev)
      if (next) out.add(id)
      else out.delete(id)
      return out
    })
  }
  const toggleAllTemplatesSelected = (next: boolean) => {
    if (!next) {
      setSelectedTemplateIds(new Set())
      return
    }
    setSelectedTemplateIds(new Set(rows.map((r) => r.id)))
  }
  const allTemplatesSelected =
    rows.length > 0 && selectedTemplateIds.size === rows.length

  const canSubmit =
    draftName.trim().length > 0 &&
    draftRules.every(
      (r) => r.redirect_url.trim().length > 0 && (r.geo || r.time),
    )

  const selectedTagTemplate = draftTagTemplateId
    ? tagTemplateById.get(draftTagTemplateId)
    : undefined

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin-dpp" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("common.backToOverview")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiBookOpen fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("templates.title", "Passport Templates")}
          </Heading>
          <Text color={sc.textMuted}>
            {t(
              "templates.subtitle",
              "Layout, branding and auto-bind rules for public DPP pages.",
            )}
          </Text>
        </Box>
        <VisionButton
          variant="brand"
          size="sm"
          onClick={() => {
            resetDraft()
            setIsOpen(true)
          }}
        >
          <FiPlus />
          <Text ml={2}>{t("templates.new", "New template")}</Text>
        </VisionButton>
      </Flex>

      <VisionCard>
        <Flex
          justify="space-between"
          align="center"
          p={4}
          gap={3}
          flexWrap="wrap"
        >
          <Heading size="md" color={sc.heading}>
            {t("templates.listTitle", "Templates ({{count}})", {
              count: rows.length,
            })}
          </Heading>
          {selectedTemplateIds.size > 0 && (
            <VisionButton
              variant="ghost"
              size="sm"
              onClick={() =>
                deleteTemplatesMutation.mutate(Array.from(selectedTemplateIds))
              }
              disabled={deleteTemplatesMutation.isPending}
            >
              <FiTrash2 />
              <Text ml={2}>
                {t("templates.deleteSelected", "Delete {{count}} selected", {
                  count: selectedTemplateIds.size,
                })}
              </Text>
            </VisionButton>
          )}
        </Flex>
        {templatesQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : rows.length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {t(
              "templates.empty",
              "No passport templates in this organisation yet.",
            )}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader w="40px">
                    <Checkbox.Root
                      checked={allTemplatesSelected}
                      onCheckedChange={(d) =>
                        toggleAllTemplatesSelected(!!d.checked)
                      }
                      size="sm"
                      aria-label="Select all templates"
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                    </Checkbox.Root>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("templates.col.name", "Name")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("templates.col.default", "Default")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("templates.col.enabled", "Enabled")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("templates.col.tagTemplate", "Tag Template")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("templates.col.rules", "Rules")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader w="40px" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((tpl) => {
                  const boundTagTemplate = tpl.auto_bind_tag_template_id
                    ? tagTemplateById.get(tpl.auto_bind_tag_template_id)
                    : undefined
                  const isSelected = selectedTemplateIds.has(tpl.id)
                  return (
                    <Table.Row key={tpl.id}>
                      <Table.Cell>
                        <Checkbox.Root
                          checked={isSelected}
                          onCheckedChange={(d) =>
                            toggleTemplateSelection(tpl.id, !!d.checked)
                          }
                          size="sm"
                          aria-label={`Select ${tpl.name}`}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>
                      </Table.Cell>
                      <Table.Cell color={sc.heading} fontSize="sm">
                        {tpl.name}
                      </Table.Cell>
                      <Table.Cell>
                        {tpl.is_default && (
                          <Badge colorPalette="blue" size="sm">
                            {t("templates.defaultBadge", "Default")}
                          </Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          colorPalette={tpl.enabled ? "green" : "gray"}
                          size="sm"
                        >
                          {tpl.enabled
                            ? t("templates.enabledBadge", "On")
                            : t("templates.disabledBadge", "Off")}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted} fontSize="sm">
                        {boundTagTemplate
                          ? boundTagTemplate.name
                          : (tpl.auto_bind_tag_template_id ?? "—")}
                      </Table.Cell>
                      <Table.Cell color={sc.heading} fontSize="sm">
                        {tpl.rules?.length ?? 0}
                      </Table.Cell>
                      <Table.Cell>
                        <VisionButton
                          variant="ghost"
                          size="xs"
                          onClick={() => seedDraftFromTemplate(tpl)}
                          aria-label={`Edit ${tpl.name}`}
                        >
                          <FiEdit2 />
                        </VisionButton>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VisionCard>

      {/* --- Create / Edit dialog --- */}
      <DialogRoot
        open={isOpen}
        onOpenChange={(e) => setIsOpen(e.open)}
        size="xl"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId === null
                ? t("templates.dialogTitle", "New passport template")
                : t("templates.editDialogTitle", "Edit passport template")}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={4}>
              {/* DPP -> Tag Template -> Tag Type hierarchy note */}
              <Box
                p={3}
                borderRadius="md"
                bg={sc.eventBg}
                border="1px solid"
                borderColor={sc.border}
              >
                <Text fontSize="xs" color={sc.textMuted}>
                  {t(
                    "templates.hierarchyNote",
                    "Binding order is DPP Template → Tag Template → Tag Type. When a tag is scanned, QDat resolves the operational Tag Template by tag_type (Opus, NFC_V, Generic, …) first; this DPP template then auto-binds to that Tag Template (if you pick one below) before falling back to per-axis matchers (chip_manufacturer / cin_brand / nfc_ic_manufacturer / tag_type) and finally the org default. Pick a Tag Template here to lock the cascade.",
                  )}
                </Text>
              </Box>

              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("templates.field.name", "Name")}
                </Text>
                <Input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="Cooltag DPP"
                />
              </Box>

              <Flex gap={4} flexWrap="wrap" align="center">
                <Checkbox.Root
                  checked={draftEnabled}
                  onCheckedChange={(d) => setDraftEnabled(!!d.checked)}
                  size="sm"
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>
                    {t("templates.field.enabled", "Enabled")}
                  </Checkbox.Label>
                </Checkbox.Root>
              </Flex>

              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("templates.field.tagTemplate", "Bind to Tag Template")}
                </Text>
                <select
                  value={draftTagTemplateId}
                  onChange={(e) => setDraftTagTemplateId(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.06)",
                    color: "inherit",
                  }}
                >
                  <option value="" style={{ background: "#1a202c" }}>
                    {t(
                      "templates.field.noTagTemplate",
                      "— (auto-bind via payload axes) —",
                    )}
                  </option>
                  {tagTemplates.map((tt) => (
                    <option
                      key={tt.id}
                      value={tt.id}
                      style={{ background: "#1a202c" }}
                    >
                      {tt.name}
                      {tt.tag_type_match ? `  ·  ${tt.tag_type_match}` : ""}
                    </option>
                  ))}
                </select>
                {selectedTagTemplate?.tag_type_match && (
                  <Flex mt={2} gap={2} align="center" flexWrap="wrap">
                    <Text fontSize="xs" color={sc.textMuted}>
                      {t("templates.field.matchesTagType", "Matches tag_type:")}
                    </Text>
                    <Badge colorPalette="purple" size="sm">
                      {selectedTagTemplate.tag_type_match}
                    </Badge>
                  </Flex>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t(
                    "templates.field.fallbackDppRecord",
                    "Fallback DPP record",
                  )}
                </Text>
                <select
                  value={draftDefaultDppRecordId}
                  onChange={(e) =>
                    setDraftDefaultDppRecordId(e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.06)",
                    color: "inherit",
                  }}
                >
                  <option value="" style={{ background: "#1a202c" }}>
                    {t(
                      "templates.field.noFallbackDppRecord",
                      "— (none) —",
                    )}
                  </option>
                  {dppRecords.map((rec) => (
                    <option
                      key={rec.id}
                      value={rec.id}
                      style={{ background: "#1a202c" }}
                    >
                      {rec.gs1_sku}
                    </option>
                  ))}
                </select>
                <Text fontSize="xs" color={sc.textMuted} mt={1}>
                  {t(
                    "templates.field.fallbackDppRecordHint",
                    "Primary join is Tag.sku → DPP record GS1 SKU. This fallback is used when a tag's SKU is blank or matches no record. Set Tag.sku per tag on the tag edit page or on the Tag SKUs admin page.",
                  )}
                </Text>
              </Box>

              {/*
               * Payload axes are only consulted by the resolver when no
               * Tag Template is bound above. Hide the whole section
               * once the operator has picked a Tag Template so the
               * dialog doesn't pretend these fields still matter.
               */}
              {!draftTagTemplateId && (
              <Box>
                <Text fontSize="sm" color={sc.heading} mb={1} fontWeight="600">
                  {t(
                    "templates.field.autoBindAxesTitle",
                    "Auto-bind by payload",
                  )}
                </Text>
                <Text fontSize="xs" color={sc.textMuted} mb={3}>
                  {t(
                    "templates.field.autoBindAxesHint",
                    "Used only when no Tag Template is bound above. Tried in this order: chip manufacturer → CIN brand → NFC IC manufacturer → tag type → org default. Leave a field blank to skip it.",
                  )}
                </Text>
                <Stack gap={3}>
                  <Box>
                    <Text fontSize="xs" color={sc.textMuted} mb={1}>
                      {t(
                        "templates.field.chipManufacturer",
                        "Chip manufacturer",
                      )}
                    </Text>
                    <CatalogSelect
                      value={draftChipManufacturer}
                      onChange={setDraftChipManufacturer}
                      options={catalogOptions.chip_manufacturer}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color={sc.textMuted} mb={1}>
                      {t("templates.field.cinBrand", "CIN brand")}
                    </Text>
                    <CatalogSelect
                      value={draftCinBrand}
                      onChange={setDraftCinBrand}
                      options={catalogOptions.cin_brand}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color={sc.textMuted} mb={1}>
                      {t(
                        "templates.field.nfcIcManufacturer",
                        "NFC IC manufacturer",
                      )}
                    </Text>
                    <CatalogSelect
                      value={draftNfcIcManufacturer}
                      onChange={setDraftNfcIcManufacturer}
                      options={catalogOptions.nfc_ic_manufacturer}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color={sc.textMuted} mb={1}>
                      {t("templates.field.tagType", "Tag type")}
                    </Text>
                    <CatalogSelect
                      value={draftTagType}
                      onChange={setDraftTagType}
                      options={catalogOptions.tag_type}
                    />
                  </Box>
                </Stack>
              </Box>
              )}

              <Box>
                <Flex justify="space-between" align="center" mb={2} gap={2}>
                  <Text fontSize="sm" color={sc.textMuted}>
                    {t("templates.field.rules", "Rewrite rules")}
                  </Text>
                  <Flex gap={2}>
                    {selectedRuleIndices.size > 0 && (
                      <VisionButton
                        variant="ghost"
                        size="xs"
                        onClick={removeSelectedRules}
                      >
                        <FiTrash2 />
                        <Text ml={1}>
                          {t(
                            "templates.rule.deleteSelected",
                            "Delete {{count}}",
                            { count: selectedRuleIndices.size },
                          )}
                        </Text>
                      </VisionButton>
                    )}
                    <VisionButton variant="ghost" size="xs" onClick={addRule}>
                      <FiPlus />
                      <Text ml={1}>{t("templates.rule.add", "Add rule")}</Text>
                    </VisionButton>
                  </Flex>
                </Flex>

                {draftRules.length === 0 ? (
                  <Text fontSize="xs" color={sc.textMuted}>
                    {t(
                      "templates.rule.empty",
                      "No rules yet. Add a rule with a geofence, an elapsed-time condition, or both — when every condition matches, visitors are redirected to the rule's URL instead of seeing the passport.",
                    )}
                  </Text>
                ) : (
                  <Stack gap={3}>
                    <Flex
                      align="center"
                      gap={2}
                      px={1}
                      color={sc.textMuted}
                      fontSize="xs"
                    >
                      <Checkbox.Root
                        checked={allRulesSelected}
                        onCheckedChange={(d) =>
                          toggleAllRulesSelected(!!d.checked)
                        }
                        size="sm"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                      </Checkbox.Root>
                      <Text>
                        {t(
                          "templates.rule.selectAll",
                          "Select all ({{count}})",
                          { count: draftRules.length },
                        )}
                      </Text>
                      <Box ml="auto">
                        {t(
                          "templates.rule.priorityHint",
                          "Priority: geo-bearing rules win over time-only on ties; otherwise first match.",
                        )}
                      </Box>
                    </Flex>
                    {draftRules.map((rule, idx) => (
                      <RuleCard
                        key={idx}
                        rule={rule}
                        selected={selectedRuleIndices.has(idx)}
                        onSelectedChange={(next) =>
                          toggleRuleSelection(idx, next)
                        }
                        onPatch={(p) => patchRule(idx, p)}
                        onRemove={() => removeRule(idx)}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Flex w="100%" justify="space-between" gap={2}>
              <Flex>
                {editingId !== null && (
                  <VisionButton
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (
                        typeof window !== "undefined" &&
                        window.confirm(
                          t(
                            "templates.confirmDelete",
                            "Delete this passport template?",
                          ),
                        )
                      ) {
                        deleteTemplatesMutation.mutate([editingId])
                      }
                    }}
                    disabled={deleteTemplatesMutation.isPending}
                  >
                    <FiTrash2 />
                    <Text ml={2}>{t("common.delete", "Delete")}</Text>
                  </VisionButton>
                )}
              </Flex>
              <Flex gap={2}>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                >
                  {t("common.cancel", "Cancel")}
                </VisionButton>
                <VisionButton
                  variant="brand"
                  size="sm"
                  onClick={() => saveMutation.mutate()}
                  disabled={!canSubmit || saveMutation.isPending}
                >
                  {saveMutation.isPending
                    ? t("common.saving", "Saving…")
                    : editingId === null
                      ? t("common.create", "Create")
                      : t("common.save", "Save")}
                </VisionButton>
              </Flex>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Small <select> wrapper that always includes the current draft value as
// an option even when it isn't in the catalog — so loading a template
// with a hand-typed legacy value (e.g. a vendor string that pre-dates
// the catalog) doesn't silently blank the field on save.
// ---------------------------------------------------------------------------
function CatalogSelect({
  value,
  onChange,
  options,
}: {
  value: string
  onChange: (next: string) => void
  options: string[]
}) {
  const merged = useMemo(() => {
    const set = new Set(options)
    if (value && !set.has(value)) return [value, ...options]
    return options
  }, [value, options])
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        padding: "8px",
        borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.12)",
        background: "rgba(255,255,255,0.06)",
        color: "inherit",
      }}
    >
      <option value="" style={{ background: "#1a202c" }}>
        — (any) —
      </option>
      {merged.map((v) => (
        <option key={v} value={v} style={{ background: "#1a202c" }}>
          {v}
        </option>
      ))}
    </select>
  )
}

// ---------------------------------------------------------------------------
// One row in the rules editor — collapsible blocks for geo + time, both
// optional but at least one required.
// ---------------------------------------------------------------------------
function RuleCard({
  rule,
  selected,
  onSelectedChange,
  onPatch,
  onRemove,
}: {
  rule: DppRule
  selected: boolean
  onSelectedChange: (next: boolean) => void
  onPatch: (patch: Partial<DppRule>) => void
  onRemove: () => void
}) {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")

  const hasGeo = !!rule.geo
  const hasTime = !!rule.time

  const toggleGeo = (next: boolean) => {
    onPatch(next ? { geo: rule.geo ?? emptyGeoBlock() } : { geo: undefined })
  }
  const toggleTime = (next: boolean) => {
    onPatch(
      next ? { time: rule.time ?? emptyTimeBlock() } : { time: undefined },
    )
  }

  return (
    <VisionCard variant="outline">
      <Stack gap={3} p={3}>
        <Flex justify="space-between" align="center" gap={2}>
          <Flex gap={2} align="center" flexWrap="wrap">
            <Checkbox.Root
              checked={selected}
              onCheckedChange={(d) => onSelectedChange(!!d.checked)}
              size="sm"
              aria-label="Select rule"
              display="inline-flex"
              alignItems="center"
              gap={2}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label lineHeight="1">
                {t(
                  "templates.rule.selectForDeletion",
                  "Select for deletion",
                )}
              </Checkbox.Label>
            </Checkbox.Root>
            {hasGeo && (
              <Badge colorPalette="purple" size="sm">
                {t("templates.rule.geo", "Geofence")}
              </Badge>
            )}
            {hasTime && (
              <Badge colorPalette="orange" size="sm">
                {t("templates.rule.time", "Time elapsed")}
              </Badge>
            )}
            {hasGeo && hasTime && (
              <Badge colorPalette="blue" size="sm">
                {t("templates.rule.combo", "Spatiotemporal (AND)")}
              </Badge>
            )}
          </Flex>
          <VisionButton variant="ghost" size="xs" onClick={onRemove}>
            <FiTrash2 />
          </VisionButton>
        </Flex>

        <Input
          placeholder={t(
            "templates.rule.labelPlaceholder",
            "Label (e.g. 'beyond warranty at depot')",
          )}
          value={rule.label ?? ""}
          onChange={(e) => onPatch({ label: e.target.value })}
          size="sm"
        />

        <Flex gap={4} flexWrap="wrap">
          <Checkbox.Root
            checked={hasGeo}
            onCheckedChange={(d) => toggleGeo(!!d.checked)}
            size="sm"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>
              {t("templates.rule.useGeo", "Geofence condition")}
            </Checkbox.Label>
          </Checkbox.Root>
          <Checkbox.Root
            checked={hasTime}
            onCheckedChange={(d) => toggleTime(!!d.checked)}
            size="sm"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>
              {t("templates.rule.useTime", "Time condition")}
            </Checkbox.Label>
          </Checkbox.Root>
        </Flex>

        {hasGeo && rule.geo && (
          <Box>
            <Text fontSize="xs" color={sc.textMuted} mb={1}>
              {t(
                "templates.rule.geoHint",
                "Fires when the tag's last MQTT-reported location falls within the circle.",
              )}
            </Text>
            <GeofencePicker
              value={rule.geo as GeofenceValue}
              onChange={(next) => onPatch({ geo: next })}
            />
          </Box>
        )}

        {hasTime && rule.time && (
          <Box>
            <Text fontSize="xs" color={sc.textMuted} mb={1}>
              {t(
                "templates.rule.timeHint",
                "Fires when this much time has elapsed since the tag was last scanned.",
              )}
            </Text>
            <Flex gap={2}>
              <Box flex="1">
                <Text fontSize="xs" color={sc.textMuted}>
                  Amount
                </Text>
                <Input
                  type="number"
                  min={1}
                  size="sm"
                  value={rule.time.after_amount}
                  onChange={(e) =>
                    onPatch({
                      time: {
                        ...(rule.time as RuleTimeBlock),
                        after_amount: Math.max(1, Number(e.target.value)),
                      },
                    })
                  }
                />
              </Box>
              <Box flex="1">
                <Text fontSize="xs" color={sc.textMuted}>
                  Unit
                </Text>
                <select
                  value={rule.time.after_unit}
                  onChange={(e) =>
                    onPatch({
                      time: {
                        ...(rule.time as RuleTimeBlock),
                        after_unit: e.target.value as TimeUnit,
                      },
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "6px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.06)",
                    color: "inherit",
                  }}
                >
                  <option value="minutes" style={{ background: "#1a202c" }}>
                    minutes
                  </option>
                  <option value="hours" style={{ background: "#1a202c" }}>
                    hours
                  </option>
                  <option value="days" style={{ background: "#1a202c" }}>
                    days
                  </option>
                </select>
              </Box>
            </Flex>
          </Box>
        )}

        <Box>
          <Text fontSize="xs" color={sc.textMuted}>
            Redirect URL
          </Text>
          <Flex align="center" gap={1}>
            <Textarea
              size="sm"
              rows={1}
              flex="1"
              value={rule.redirect_url}
              onChange={(e) => onPatch({ redirect_url: e.target.value })}
              placeholder="https://example.com/products"
            />
            <Text
              fontFamily="mono"
              fontSize="sm"
              color={sc.textMuted}
              whiteSpace="nowrap"
              px={2}
            >
              {"/<EPC>"}
            </Text>
          </Flex>
          <Text fontSize="xs" color={sc.textMuted} mt={1}>
            The tag's EPC (or NFC UID) is appended automatically at redirect
            time — leave it out of what you type. Any trailing "/" in your URL
            is stripped.
          </Text>
          <Checkbox.Root
            mt={3}
            checked={rule.redirect_public_page}
            onCheckedChange={(d) =>
              onPatch({ redirect_public_page: !!d.checked })
            }
            size="sm"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>
              {t(
                "templates.rule.redirectPublicPage",
                "Also redirect public DPP page on match",
              )}
            </Checkbox.Label>
          </Checkbox.Root>
          <Text fontSize="xs" color={sc.textMuted} mt={1}>
            {t(
              "templates.rule.redirectPublicPageHint",
              "When off (default), the URL is pushed only to the device over MQTT — the public DPP page keeps rendering. Turn on to also send browser visitors to this URL when the rule fires.",
            )}
          </Text>
        </Box>
      </Stack>
    </VisionCard>
  )
}
